import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { MessageSquare, Star, RefreshCw, Trash2, CheckCircle, XCircle, Clock } from 'lucide-react';

export default function AdminTestimonials({
  testimonials,
  statusCounts,
  filter,
  setFilter,
  onLoadTestimonials,
  onUpdateTestimonial,
  updatingTestimonial,
  onDeleteTestimonial,
}) {
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  const handleOpenDetail = (testimonial) => {
    setSelectedTestimonial({ ...testimonial });
    setShowDetailDialog(true);
  };

  const handleSaveChanges = async () => {
    if (selectedTestimonial) {
      await onUpdateTestimonial(selectedTestimonial.id, {
        status: selectedTestimonial.status,
        admin_notes: selectedTestimonial.admin_notes,
        featured: selectedTestimonial.featured
      });
      setShowDetailDialog(false);
    }
  };

  const handleQuickAction = async (testimonial, status) => {
    await onUpdateTestimonial(testimonial.id, { status });
  };

  const handleDelete = () => {
    if (selectedTestimonial) {
      onDeleteTestimonial(selectedTestimonial.id);
      setShowDetailDialog(false);
    }
  };

  return (
    <>
      <Card className="rounded-sm bg-slate-800 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-yellow-400" />
            Testimonials
            {statusCounts.pending > 0 && (
              <Badge className="bg-yellow-600 ml-2">{statusCounts.pending} pending</Badge>
            )}
          </CardTitle>
          <CardDescription className="text-slate-400">
            Review and publish customer testimonials
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Status Summary */}
          <div className="flex gap-2 mb-4">
            <Badge className="bg-yellow-600">{statusCounts.pending || 0} Pending</Badge>
            <Badge className="bg-green-600">{statusCounts.approved || 0} Approved</Badge>
            <Badge className="bg-red-600">{statusCounts.rejected || 0} Rejected</Badge>
          </div>

          <div className="flex items-center justify-between mb-4">
            <Select 
              value={filter || 'all'} 
              onValueChange={(val) => setFilter(val === 'all' ? '' : val)}
            >
              <SelectTrigger className="w-32 rounded-sm bg-slate-700 border-slate-600 text-white text-sm">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={onLoadTestimonials} className="rounded-sm border-slate-600">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {testimonials.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No testimonials yet</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="p-4 bg-slate-700 rounded-sm hover:bg-slate-600 cursor-pointer"
                  onClick={() => handleOpenDetail(testimonial)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-200">{testimonial.name}</span>
                        <div className="flex gap-0.5">
                          {[...Array(testimonial.rating || 5)].map((_, i) => (
                            <Star key={i} className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      </div>
                      <p className="text-sm text-slate-400 mb-2">
                        {testimonial.role && `${testimonial.role}, `}{testimonial.company}
                      </p>
                      <p className="text-sm text-slate-300 line-clamp-2 italic">"{testimonial.quote}"</p>
                      <p className="text-xs text-slate-500 mt-2">
                        {new Date(testimonial.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge className={
                        testimonial.status === 'pending' ? 'bg-yellow-600' :
                        testimonial.status === 'approved' ? 'bg-green-600' : 'bg-red-600'
                      }>
                        {testimonial.status}
                      </Badge>
                      {testimonial.status === 'pending' && (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-green-400 hover:text-green-300 hover:bg-green-900/20"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuickAction(testimonial, 'approved');
                            }}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleQuickAction(testimonial, 'rejected');
                            }}
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Testimonial Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-yellow-400" />
              Testimonial Details
            </DialogTitle>
          </DialogHeader>
          {selectedTestimonial && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[...Array(selectedTestimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 text-xs">Name</p>
                  <p className="font-medium">{selectedTestimonial.name}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Email</p>
                  <p className="text-sm text-blue-400">{selectedTestimonial.email}</p>
                </div>
              </div>
              
              {(selectedTestimonial.company || selectedTestimonial.role) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedTestimonial.company && (
                    <div>
                      <p className="text-slate-400 text-xs">Company</p>
                      <p className="text-sm">{selectedTestimonial.company}</p>
                    </div>
                  )}
                  {selectedTestimonial.role && (
                    <div>
                      <p className="text-slate-400 text-xs">Role</p>
                      <p className="text-sm">{selectedTestimonial.role}</p>
                    </div>
                  )}
                </div>
              )}
              
              <div>
                <p className="text-slate-400 text-xs mb-1">Testimonial</p>
                <p className="text-sm bg-slate-700 p-3 rounded-sm italic">&quot;{selectedTestimonial.quote}&quot;</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 text-xs mb-1">Status</p>
                  <Select
                    value={selectedTestimonial.status}
                    onValueChange={(val) => setSelectedTestimonial({ ...selectedTestimonial, status: val })}
                  >
                    <SelectTrigger className="w-full rounded-sm bg-slate-700 border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">
                        <span className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-yellow-500" />
                          Pending
                        </span>
                      </SelectItem>
                      <SelectItem value="approved">
                        <span className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          Approved
                        </span>
                      </SelectItem>
                      <SelectItem value="rejected">
                        <span className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-500" />
                          Rejected
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-slate-400 text-xs mb-1">Submitted</p>
                  <p className="text-sm py-2">{new Date(selectedTestimonial.created_at).toLocaleString()}</p>
                </div>
              </div>
              
              <div>
                <p className="text-slate-400 text-xs mb-1">Admin Notes</p>
                <Textarea
                  value={selectedTestimonial.admin_notes || ''}
                  onChange={(e) => setSelectedTestimonial({ ...selectedTestimonial, admin_notes: e.target.value })}
                  placeholder="Add internal notes..."
                  className="rounded-sm bg-slate-700 border-slate-600 text-white"
                  rows={2}
                />
              </div>
              
              <div className="flex justify-between pt-2">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDelete}
                  className="rounded-sm"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="rounded-sm border-slate-600">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveChanges}
                    disabled={updatingTestimonial}
                    className="rounded-sm bg-blue-600 hover:bg-blue-700"
                  >
                    {updatingTestimonial ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
